import { useState, useEffect } from "react";
import { ListLayout } from "../../../layouts";
import {
  history,
  MensagemPersonalizadaService,
  notification,
  SessionService,
  OrdemServicosStatusPersonalizadosService, // ← Adicionado a importação do service
} from "../../../services";
import { DataGrid } from "../../../components";
import { FormFiltroAvancado } from "./components";
import { cacheApi, formatDate, responseErros } from "../../../utils";
import { filtrosUrl, PermisoesHelper } from "../../../helpers";
import { useCacheContext } from "../../../contexts/cache.context";
import {
  contextos,
  contextosCobranca,
  contextosLote,
} from "../resources/constantes";
import { format } from "date-fns";
import { capitalize } from "lodash";

const mensagemPersonalizadaService = new MensagemPersonalizadaService();
const ordemServicosStatusPersonalizadosService =
  new OrdemServicosStatusPersonalizadosService();

const ListMensagemPersonalizadaView = () => {
  const permissoesHelper = new PermisoesHelper();
  const sessionService = new SessionService();
  permissoesHelper.validarPermisao("mensagem-personalizada-visualizar");
  const dadosEmpresa = sessionService.getEmpresa();
  const { cacheDto, setCacheDto } = useCacheContext();
  const [loading, setLoading] = useState(false);
  const [mensagensPersonalizadas, setMensagensPersonalizadas] = useState([]);
  const [rowCount, setRowCount] = useState(null);
  const [statusOSList, setStatusOSList] = useState([]);

  const contextoGeral = [...contextos, ...contextosLote, ...contextosCobranca];

  const colunas = [
    {
      field: "contexto",
      headerName: "Contexto da Mensagem",
      flex: 500,
      filtrar: true,
      valueGetter: (params) => {
        const exactMatch = contextoGeral.find(
          (contexto) => contexto.value === params.value,
        );
        if (exactMatch) return exactMatch.label;

        if (params.value?.startsWith("ordem-servico-")) {
          const statusSlug = params.value
            .replace("ordem-servico-", "")
            .toLowerCase();
          const statusObj = statusOSList.find(
            (s) => s.descricao.toLowerCase() === statusSlug,
          );

          if (statusObj) {
            return `Ordem de Serviço - Status (${statusObj.descricao})`;
          }
          return `Ordem de Serviço - Status (${capitalize(statusSlug)})`;
        }

        return params.value;
      },
      renderCell: (params) => {
        const rowContext = params.row?.contexto || params.value;
        const exactMatch = contextoGeral.find(
          (contexto) => contexto.value === rowContext,
        );
        if (exactMatch) return <span>{exactMatch.label}</span>;

        if (rowContext?.startsWith("ordem-servico-")) {
          const statusSlug = rowContext
            .replace("ordem-servico-", "")
            .toLowerCase();
          const statusObj = statusOSList.find(
            (s) => s.descricao.toLowerCase() === statusSlug,
          );

          if (statusObj) {
            return (
              <div style={{ display: "flex", alignItems: "center" }}>
                Ordem de Serviço - Status ({statusObj.descricao}
                {statusObj.cor && (
                  <i
                    className="ph-fill ph-circle"
                    style={{
                      color: statusObj.cor,
                      fontSize: "14px",
                      marginTop: "2px",
                      marginLeft: "5px",
                    }}
                  ></i>
                )}
                )
              </div>
            );
          }
          return (
            <span>Ordem de Serviço - Status ({capitalize(statusSlug)})</span>
          );
        }

        return <span>{rowContext}</span>;
      },
    },
    {
      field: "tipo",
      headerName: "Tipo",
      flex: 500,
      filtrar: true,
      valueGetter: (params) => capitalize(params.value),
    },
    {
      field: "destinatarios",
      headerName: "Data Último Envio",
      flex: 500,
      filtrar: true,
      valueGetter: (params) =>
        params.value[0]?.dataEnvio
          ? format(
              formatDate.received(params.value[0]?.dataEnvio),
              "dd/mm/yyyy HH:mm",
            )
          : "",
    },
  ];

  useEffect(() => {
    const filtros = filtrosUrl.get();
    buscarMensagensPersonalizadas(filtros);
    buscarStatusPersonalizados();
  }, []);

  const buscarStatusPersonalizados = async () => {
    const result = await ordemServicosStatusPersonalizadosService.getAll();

    if (!result.isAxiosError) {
      const colunasCompletas = [
        { id: null, descricao: "ABERTO", cor: "#4FBB53" },
        ...result.data,
        { id: null, descricao: "FECHADO", cor: "#004780" },
      ];
      setStatusOSList(colunasCompletas);
    }
  };

  const buscarMensagensPersonalizadas = async function (filter) {
    if (!dadosEmpresa?.whatsappSimples && !dadosEmpresa?.whatsappAvancado) {
      return;
    }
    const label = filtrosUrl.getUrl(filter);
    setLoading(true);
    if (dadosEmpresa?.whatsappSimples && !dadosEmpresa?.whatsappAvancado) {
      filter.tipo = "SIMPLES";
    }
    if (dadosEmpresa?.whatsappAvancado && !dadosEmpresa?.whatsappSimples) {
      filter.tipo = "LOTE";
    }
    const cacheBody = {
      dto: { cacheDto, setCacheDto },
      apiFunction: mensagemPersonalizadaService.getAll(filter),
    };
    const result = await cacheApi(label, cacheBody);
    if (!result.isAxiosError) {
      setMensagensPersonalizadas(result.data.rows);
      setRowCount(result.data.count);
      filtrosUrl.set(filter);
    } else {
      responseErros(result);
    }
    setLoading(false);
  };

  const onClickCadastrarMensagemPersonalizada = function (descricao) {
    switch (descricao) {
      case "Mensagem em lote":
        history.push("/integracao/mensagem-personalizada/mensagem-lote");
        break;
      default:
        history.push("/integracao/mensagem-personalizada/create");
        break;
    }
  };

  const onClickEditMensagemPersonalizada = function (id) {
    history.push(`/integracao/mensagem-personalizada/${id}`);
  };

  const onClickDeleteMensagemPersonalizada = async function (id) {
    const data = mensagensPersonalizadas.find((item) => item.id === id);
    const retornoAlerta = await notification.confirmacao(
      "Excluir!",
      `Tem certeza que deseja excluir mensagem código ${data.codigo}`,
    );
    if (retornoAlerta.isConfirmed) {
      setLoading(true);
      const result = await mensagemPersonalizadaService.deletar(id);
      if (!result.isAxiosError) {
        buscarMensagensPersonalizadas();
        notification.deletadoSucesso();
        setLoading(false);
      } else {
        responseErros(result);
        setLoading(false);
      }
    }
  };

  const sendServerDatagrid = (props) => {
    if (props.tipoFiltro == "simples") {
      buscarMensagensPersonalizadas(props.filtros);
    } else {
      buscarPesquisaAvancada(props.filtros);
    }
  };

  const buscarPesquisaAvancada = async (filter) => {
    const label = filtrosUrl.getUrl(filter);
    setLoading(true);
    const porIntervalo = [];
    const filtrosAvancadosTemp = {
      ...filter,
    };
    const cacheBody = {
      dto: { cacheDto, setCacheDto },
      apiFunction: mensagemPersonalizadaService.getAllFiltroAvancado({
        ...filtrosAvancadosTemp,
        restritiva: true,
        porIntervalo,
      }),
    };
    const result = await cacheApi(label, cacheBody);
    if (!result.isAxiosError) {
      setMensagensPersonalizadas(result.data.rows);
      setRowCount(result.data.count);
      filtrosUrl.set(filter);
    } else {
      responseErros(result);
    }
    setLoading(false);
  };

  return (
    <ListLayout
      title="Mensagens Personalizadas"
      loading={loading}
      buttonSelectOpcoes={[
        {
          permission: dadosEmpresa?.whatsappAvancado,
          descricao: "Mensagem em lote",
        },
      ]}
      onClickCadastrar={onClickCadastrarMensagemPersonalizada}
      permissaoButton={
        dadosEmpresa?.whatsappSimples &&
        permissoesHelper.temPermisao("mensagem-personalizada-cadastrar")
      }
    >
      <DataGrid
        rows={mensagensPersonalizadas}
        columns={colunas}
        onClickEdit={onClickEditMensagemPersonalizada}
        onClickDelete={onClickDeleteMensagemPersonalizada}
        visualizarShow={permissoesHelper.temPermisao(
          "mensagem-personalizada-visualizar",
        )}
        editShow={permissoesHelper.temPermisao("mensagem-personalizada-editar")}
        deleteShow={permissoesHelper.temPermisao(
          "mensagem-personalizada-excluir",
        )}
        paginationMode={"server"}
        rowCount={rowCount}
        sendServer={(props) => sendServerDatagrid(props)}
        FormFiltroAvancado={FormFiltroAvancado}
      />
    </ListLayout>
  );
};

export default ListMensagemPersonalizadaView;
